import { resolveClinicianQuestionnaireReviewPromptText } from "@amplecare/contracts";

import { formatDateWithMonthName, formatPhoneDisplay } from "./record/format.js";

export type ClinicianDetailFact = {
  labelText: string;
  valueText: string;
};

export type ParsedClinicianDetailText = {
  bodyLines: string[];
  date: Date | null;
  detailRows: ClinicianDetailFact[];
  primaryText: string | null;
  statusText: string;
};

export function buildClinicianQuestionnaireEntryKey(
  sessionId: string | null | undefined,
  generation: number,
): string {
  return `${sessionId?.trim() || "manual-entry"}:${generation}`;
}

const clinicianManualContinuationStatuses = new Set([
  "calling",
  "identity_verification",
  "in_progress",
  "paused",
  "verified",
]);

// A live call means the AI is on the phone with the patient right now. These
// are exactly the statuses formatClinicianOutboundSessionStatus renders as
// "Call in progress", so the lock and the badge can never disagree.
const clinicianLiveCallStatuses = new Set([
  "calling",
  "identity_verification",
  "in_progress",
  "verified",
]);

export function resolveClinicianOutboundSessionContext<
  T extends {
    sessionId: string | null;
    sessionStatus: string | null;
  },
>(calls: readonly T[]): {
  activeCall: T | null;
  statusCall: T | null;
} {
  const activeCall =
    calls.find(
      (call) =>
        call.sessionId != null &&
        isClinicianManualContinuationSessionStatus(call.sessionStatus),
    ) ?? null;

  return {
    activeCall,
    statusCall:
      activeCall ??
      calls.find((call) => call.sessionStatus != null) ??
      null,
  };
}

function splitClinicianDetailParts(valueText: string): string[] {
  return valueText
    .split(/[|\n]+/u)
    .flatMap((part) =>
      part.split(/;\s+(?=[A-Za-z][^:]{1,48}:\s*\S)/u),
    )
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

function parseClinicianDetailFact(
  part: string,
): ClinicianDetailFact | null {
  const match = part.match(
    /^([A-Za-z][A-Za-z0-9 /&()+.'-]{1,63}):\s*(.+)$/u,
  );

  if (!match) {
    return null;
  }

  const labelText = match[1]?.trim() ?? "";
  const valueText = match[2]?.trim() ?? "";

  return labelText.length > 0 && valueText.length > 0
    ? { labelText, valueText }
    : null;
}

function readClockTime(valueText: string): {
  hour: number;
  minute: number;
} | null {
  const match = valueText.match(
    /(?:\btime\s*:\s*)?(\d{1,2})(?::(\d{2}))\s*(am|pm)?\b/iu,
  );

  if (!match) {
    return null;
  }

  let hour = Number.parseInt(match[1] ?? "0", 10);
  const minute = Number.parseInt(match[2] ?? "0", 10);
  const meridiem = match[3]?.toLowerCase();

  if (
    minute < 0 ||
    minute > 59 ||
    (meridiem ? hour < 1 || hour > 12 : hour < 0 || hour > 23)
  ) {
    return null;
  }

  if (meridiem === "pm" && hour < 12) {
    hour += 12;
  } else if (meridiem === "am" && hour === 12) {
    hour = 0;
  }

  return { hour, minute };
}

function isValidClinicianCalendarDate(
  year: number,
  month: number,
  day: number,
): boolean {
  if (
    year < 1900 ||
    year > 2200 ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31
  ) {
    return false;
  }

  const date = new Date(Date.UTC(year, month - 1, day));

  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
}

export function parseClinicianDateTimeText(valueText: string): Date | null {
  const trimmed = valueText.trim();
  const isoMatch = trimmed.match(
    /\b(20\d{2})-(\d{2})-(\d{2})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?(?:\s*(Z|[+-]\d{2}:?\d{2}))?)?\b/u,
  );

  if (isoMatch) {
    const [, year, month, day, rawHour, rawMinute, rawSecond, zone] =
      isoMatch;
    const numericYear = Number.parseInt(year ?? "0", 10);
    const numericMonth = Number.parseInt(month ?? "0", 10);
    const numericDay = Number.parseInt(day ?? "0", 10);
    const numericHour = Number.parseInt(rawHour ?? "12", 10);
    const numericMinute = Number.parseInt(rawMinute ?? "00", 10);
    const numericSecond = Number.parseInt(rawSecond ?? "00", 10);

    if (
      !isValidClinicianCalendarDate(
        numericYear,
        numericMonth,
        numericDay,
      ) ||
      numericHour < 0 ||
      numericHour > 23 ||
      numericMinute < 0 ||
      numericMinute > 59 ||
      numericSecond < 0 ||
      numericSecond > 59
    ) {
      return null;
    }

    const hour = rawHour ?? "12";
    const minute = rawMinute ?? "00";
    const second = rawSecond ?? "00";
    const dateText = zone
      ? `${year}-${month}-${day}T${hour.padStart(2, "0")}:${minute}:${second}${zone}`
      : `${year}-${month}-${day}T${hour.padStart(2, "0")}:${minute}:${second}`;
    const parsed = new Date(dateText);

    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const numericDateMatch = trimmed.match(
    /\b(0?[1-9]|1[0-2])\/(0?[1-9]|[12]\d|3[01])\/(19\d{2}|20\d{2})\b/u,
  );

  if (numericDateMatch) {
    const trailingText = trimmed.slice(
      (numericDateMatch.index ?? 0) + numericDateMatch[0].length,
    );
    const clock = readClockTime(trailingText);
    const year = Number.parseInt(numericDateMatch[3] ?? "0", 10);
    const month = Number.parseInt(numericDateMatch[1] ?? "0", 10);
    const day = Number.parseInt(numericDateMatch[2] ?? "0", 10);

    if (
      !isValidClinicianCalendarDate(year, month, day) ||
      (/\d{1,2}:\d{2}/u.test(trailingText) && clock == null)
    ) {
      return null;
    }

    const parsed = new Date(
      year,
      month - 1,
      day,
      clock?.hour ?? 12,
      clock?.minute ?? 0,
    );

    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const namedDateMatch = trimmed.match(
    /\b(?:Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:tember)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)\s+\d{1,2},\s+(?:19|20)\d{2}(?:,?\s+\d{1,2}:\d{2}\s*(?:AM|PM)?)?\b/iu,
  );

  if (!namedDateMatch) {
    return null;
  }

  const namedParts = namedDateMatch[0].match(
    /^([A-Za-z]+)\s+(\d{1,2}),\s+(\d{4})(?:,?\s+(.+))?$/u,
  );
  const monthIndexByPrefix: Record<string, number> = {
    apr: 3,
    aug: 7,
    dec: 11,
    feb: 1,
    jan: 0,
    jul: 6,
    jun: 5,
    mar: 2,
    may: 4,
    nov: 10,
    oct: 9,
    sep: 8,
  };
  const monthIndex =
    monthIndexByPrefix[namedParts?.[1]?.slice(0, 3).toLowerCase() ?? ""];
  const day = Number.parseInt(namedParts?.[2] ?? "0", 10);
  const year = Number.parseInt(namedParts?.[3] ?? "0", 10);
  const trailingText = namedParts?.[4] ?? "";
  const clock = readClockTime(trailingText);

  if (
    monthIndex == null ||
    !isValidClinicianCalendarDate(year, monthIndex + 1, day) ||
    (/\d{1,2}:\d{2}/u.test(trailingText) && clock == null)
  ) {
    return null;
  }

  return new Date(
    year,
    monthIndex,
    day,
    clock?.hour ?? 12,
    clock?.minute ?? 0,
  );
}

export function parseClinicianDetailText(
  valueText: string,
): ParsedClinicianDetailText {
  const parts = splitClinicianDetailParts(valueText);
  const parsedParts = parts.map((part) => ({
    fact: parseClinicianDetailFact(part),
    part,
  }));
  const datePart = parsedParts.find(({ fact, part }) => {
    if (
      fact &&
      /^(?:date|date and time|procedure date|scheduled date)$/iu.test(
        fact.labelText,
      )
    ) {
      return true;
    }

    return parseClinicianDateTimeText(part) != null;
  });
  const timePart = parsedParts.find(({ fact }) =>
    fact ? /^time$/iu.test(fact.labelText) : false,
  );
  const dateText = [
    datePart?.fact?.valueText ?? datePart?.part ?? "",
    timePart?.fact?.valueText ?? "",
  ]
    .filter((part) => part.length > 0)
    .join(" ");
  const date = parseClinicianDateTimeText(dateText);
  const statusText =
    parsedParts.find(({ fact }) =>
      fact ? /^status$/iu.test(fact.labelText) : false,
    )?.fact?.valueText ?? "";
  const dateLabelPattern =
    /^(?:date|date and time|procedure date|scheduled date|time)$/iu;
  const detailRows = parsedParts
    .flatMap(({ fact }) =>
      fact &&
      !/^status$/iu.test(fact.labelText) &&
      !(date != null && dateLabelPattern.test(fact.labelText))
        ? [fact]
        : [],
    )
    .filter(
      (row, index, rows) =>
        rows.findIndex(
          (candidate) =>
            candidate.labelText.toLowerCase() ===
              row.labelText.toLowerCase() &&
            candidate.valueText.toLowerCase() === row.valueText.toLowerCase(),
        ) === index,
    );
  const bodyLines = parsedParts
    .filter(({ fact, part }) => {
      if (fact) {
        return false;
      }

      return parseClinicianDateTimeText(part) == null;
    })
    .map(({ part }) => part);
  const primaryFact = detailRows.find((row) =>
    /^(?:appointment|procedure|medication|name|service|title)$/iu.test(
      row.labelText,
    ),
  );
  const primaryText = bodyLines[0] ?? primaryFact?.valueText ?? null;

  return {
    bodyLines,
    date,
    detailRows,
    primaryText,
    statusText,
  };
}

export function formatClinicianProgramContextText(valueText: string): string {
  const normalized = valueText.trim();

  if (normalized.length === 0 || /^program unassigned$/iu.test(normalized)) {
    return "Program not assigned";
  }

  if (/program$/iu.test(normalized) || /not assigned$/iu.test(normalized)) {
    return normalized;
  }

  return `${normalized} program`;
}

export function formatClinicianQuestionnaireAnswerText(
  valueText: string | null | undefined,
): string {
  const normalized = valueText?.trim() ?? "";

  if (/^true$/iu.test(normalized)) {
    return "Yes";
  }

  if (/^false$/iu.test(normalized)) {
    return "No";
  }

  return normalized.length > 0 ? normalized : "Not recorded";
}

type ClinicianQuestionnaireClinicalNoteAnswer = {
  answerStatus: string | null;
  answerText: string;
  clinicianReviewRequired: boolean;
  promptText: string;
  questionKey: string;
  redFlagTriggered: boolean;
};

function readQuestionnaireClinicalNoteString(
  record: Record<string, unknown>,
  key: string,
): string | null {
  const value = record[key];

  return typeof value === "string" && value.trim().length > 0
    ? value.trim()
    : null;
}

function collectQuestionnaireClinicalNoteAnswers(
  value: unknown,
  answers: Map<string, ClinicianQuestionnaireClinicalNoteAnswer>,
  depth = 0,
): void {
  if (depth > 6 || value == null) {
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((entry) =>
      collectQuestionnaireClinicalNoteAnswers(entry, answers, depth + 1),
    );
    return;
  }

  if (typeof value !== "object") {
    return;
  }

  const record = value as Record<string, unknown>;
  const questionKey = readQuestionnaireClinicalNoteString(
    record,
    "questionKey",
  );
  const answerStatus = readQuestionnaireClinicalNoteString(
    record,
    "answerStatus",
  );
  const answerText =
    readQuestionnaireClinicalNoteString(record, "answerSummary") ??
    readQuestionnaireClinicalNoteString(record, "rawAnswerText");

  if (questionKey) {
    if (answerText || answerStatus === "redacted") {
      const fallbackPrompt = questionKey
        .replaceAll("_", " ")
        .replaceAll("-", " ")
        .replace(/\s+/gu, " ")
        .trim();
      const promptText =
        readQuestionnaireClinicalNoteString(record, "promptText") ??
        readQuestionnaireClinicalNoteString(record, "clinicianReviewLabel") ??
        `${fallbackPrompt.charAt(0).toUpperCase()}${fallbackPrompt.slice(1)}`;

      const nextAnswer = {
        answerStatus,
        answerText:
          answerStatus === "redacted"
            ? "Redacted"
            : formatClinicianQuestionnaireAnswerText(answerText),
        clinicianReviewRequired: record.clinicianReviewRequired === true,
        promptText: resolveClinicianQuestionnaireReviewPromptText(
          questionKey,
          promptText,
        ),
        questionKey,
        redFlagTriggered: record.redFlagTriggered === true,
      };
      const existingAnswer = answers.get(questionKey);

      // A redaction is terminal for display. Preserve it even if a legacy
      // envelope also exposes historical answers in a sibling collection.
      if (
        existingAnswer?.answerStatus !== "redacted" ||
        nextAnswer.answerStatus === "redacted"
      ) {
        answers.set(questionKey, nextAnswer);
      }
    }

    // A question object is the authoritative current answer. Nested values can
    // contain historical answerEvents and must never overwrite it.
    return;
  }

  Object.entries(record).forEach(([key, entry]) => {
    if (/^(?:answerEvents|answerHistory|historicalAnswers)$/iu.test(key)) {
      return;
    }

    collectQuestionnaireClinicalNoteAnswers(entry, answers, depth + 1);
  });
}

export function formatClinicianQuestionnaireClinicalNoteText(
  bodyText: string,
): string | null {
  const normalizedBodyText = bodyText
    .trim()
    .replace(/^\s*JSON:\s*/iu, "");

  if (
    normalizedBodyText.length < 2 ||
    (!normalizedBodyText.startsWith("{") &&
      !normalizedBodyText.startsWith("["))
  ) {
    return null;
  }

  try {
    const parsedBody = JSON.parse(normalizedBodyText) as unknown;
    const answers = new Map<
      string,
      ClinicianQuestionnaireClinicalNoteAnswer
    >();

    collectQuestionnaireClinicalNoteAnswers(parsedBody, answers);

    if (answers.size === 0) {
      return null;
    }

    return [...answers.values()]
      .map((answer) => {
        const reviewLabel = answer.redFlagTriggered
          ? " (red flag)"
          : answer.clinicianReviewRequired
            ? " (review required)"
            : "";

        return `Q: ${answer.promptText}\nA: ${answer.answerText}${reviewLabel}`;
      })
      .join("\n");
  } catch {
    return null;
  }
}

/**
 * True while the AI is mid-call with the patient. Questionnaire editing is
 * locked for these statuses so a clinician cannot race the patient's own
 * answers into the same session. A paused session is NOT live — that is the
 * manual-continuation workflow and stays editable.
 *
 * Unknown/absent statuses return false: a missing status almost always means
 * "no call exists", and failing closed would block the nurse's primary tool on
 * incomplete data. The authoritative guard is server-side.
 */
export function isClinicianLiveCallSessionStatus(
  statusText: string | null | undefined,
): boolean {
  return clinicianLiveCallStatuses.has(
    statusText?.trim().toLowerCase() ?? "",
  );
}

export function isClinicianManualContinuationSessionStatus(
  statusText: string | null | undefined,
): boolean {
  return clinicianManualContinuationStatuses.has(
    statusText?.trim().toLowerCase() ?? "",
  );
}

export function formatClinicianOutboundSessionStatus(
  statusText: string | null | undefined,
): string {
  const normalized = statusText?.trim().toLowerCase() ?? "";

  if (normalized === "paused") {
    return "Call paused";
  }

  if (
    normalized === "calling" ||
    normalized === "identity_verification" ||
    normalized === "in_progress" ||
    normalized === "verified"
  ) {
    return "Call in progress";
  }

  if (normalized === "scheduled") {
    return "Call scheduled";
  }

  if (normalized === "review_required") {
    return "Review required";
  }

  if (normalized === "completed") {
    return "Completed";
  }

  if (normalized === "failed") {
    return "Call failed";
  }

  if (normalized === "cancelled") {
    return "Cancelled";
  }

  if (normalized === "not_started" || normalized.length === 0) {
    return "Not started";
  }

  return normalized
    .split("_")
    .filter((part) => part.length > 0)
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(" ");
}

export function buildClinicianPatientIdentityText(input: {
  dateOfBirth?: string | null;
  hasPatientRecord: boolean;
  identifier?: string | null;
  mrn?: string | null;
  phone?: string | null;
}): string | null {
  if (input.hasPatientRecord) {
    return [
      `MRN ${input.mrn?.trim() || "not recorded"}`,
      `DOB ${formatDateWithMonthName(input.dateOfBirth?.trim() || null) ?? "not recorded"}`,
      formatPhoneDisplay(input.phone?.trim() || null) ?? "No phone recorded",
    ].join(" · ");
  }

  const formattedPhone =
    formatPhoneDisplay(input.phone?.trim() || null) ?? null;
  const parts = [
    input.identifier?.trim()
      ? `ID ${input.identifier.trim()}`
      : null,
    formattedPhone ? `Phone ${formattedPhone}` : null,
  ].filter((part): part is string => part != null);

  return parts.length > 0 ? parts.join(" · ") : null;
}
